import classnames from 'classnames';

import { CallToActionButton } from '@components/callToAction/CallToActionButton';
import { Image } from '@components/Image/Image';
import { Markdown } from '@components/markdown/Markdown';
import { NewsletterSection } from '@components/newsletterSection/NewsletterSection';
import { SoundCloudEmbed } from '@components/soundCloudEmbed/SoundCloudEmbed';
import { SpotifyEmbed } from '@components/spotifyEmbed/SpotifyEmbed';
import { TikTokEmbed } from '@components/tikTokEmbed/TikTokEmbed';
import { YouTubeEmbed } from '@components/youTubeEmbed/YouTubeEmbed';

import {
	CallToActionLinkRecord,
	DatoNewsletterRecord,
	DatoZineBlock,
} from '@interfaces';

import styles from './zine-post-body.module.scss';

const renderBlock = (block: DatoZineBlock) => {
	const { id: key, __typename: typename } = block;

	switch (typename) {
		case 'EmbedRecord': {
			const { hostname } = new URL(block.url);

			if (hostname.endsWith('youtube.com') || hostname.endsWith('youtu.be')) {
				return (
					<YouTubeEmbed
						className={styles['youtube-embed']}
						embed={block}
						key={key}
					/>
				);
			}

			if (hostname.endsWith('tiktok.com')) {
				return (
					<TikTokEmbed
						className={styles['tiktok-embed']}
						embed={block}
						key={key}
					/>
				);
			}

			if (hostname.endsWith('soundcloud.com')) {
				return (
					<SoundCloudEmbed
						className={styles['soundcloud-embed']}
						embed={block}
						key={key}
					/>
				);
			}

			if (hostname.endsWith('spotify.com')) {
				return (
					<SpotifyEmbed
						className={styles['spotify-embed']}
						embed={block}
						key={key}
					/>
				);
			}

			return null;
		}
		case 'ImageRecord': {
			return (
				<Image
					className={styles.image}
					image={block.image}
					key={key}
					loading="lazy"
					sizes="(min-width: 56.25em) calc(100vw - 8rem), calc(100vw - 2rem)"
				/>
			);
		}
		case 'PullQuoteRecord': {
			return (
				<blockquote
					className={classnames(styles['pull-quote'], 'font-t1')}
					key={key}
				>
					{block.text}
				</blockquote>
			);
		}
		case 'TextRecord': {
			// Uses the HTML editor in Dato so we need to use `dangerouslySetInnerHTML`
			return (
				<Markdown
					className={classnames(styles.text, 'markdown')}
					isZinePostBody
					key={key}
					text={block.text}
				/>
			);
		}
		case 'CallToActionLinkRecord':
			return (
				<CallToActionButton key={key} {...(block as CallToActionLinkRecord)} />
			);
		case 'NewsletterRecord':
			return (
				<NewsletterSection
					className={styles.newsletter}
					isCompact={false}
					key={key}
					{...(block as DatoNewsletterRecord)}
					location="website zine post"
				/>
			);
		default:
			return null;
	}
};

type ZinePostBodyProps = {
	blocks: DatoZineBlock[];
	className?: string;
};

export const ZinePostBody = ({ blocks, className }: ZinePostBodyProps) => {
	return (
		<section className={classnames(className, styles.blocks)}>
			{blocks.map((block) => {
				return renderBlock(block);
			})}
		</section>
	);
};
